import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Key, Copy, Plus, Clock } from 'lucide-react';
import type { ApiKey } from '@/lib/types';

interface ApiKeysProps {
  apiKeys: ApiKey[];
  onCreateKey: () => void;
  onRevokeKey: (keyId: string) => void;
}

export function ApiKeys({ apiKeys, onCreateKey, onRevokeKey }: ApiKeysProps) {
  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle>API Keys</CardTitle>
        <Button onClick={onCreateKey}>
          <Plus className="mr-2 h-4 w-4" />
          Create API Key
        </Button>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {apiKeys.map((apiKey) => (
            <div
              key={apiKey.id}
              className="p-4 rounded-lg border space-y-4"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Key className="h-4 w-4 text-primary" />
                  <h3 className="font-medium">{apiKey.name}</h3>
                </div>
                <Badge
                  variant={apiKey.status === 'active' ? 'default' : 'destructive'}
                >
                  {apiKey.status}
                </Badge>
              </div>

              <div className="flex items-center gap-2 bg-muted p-2 rounded">
                <code className="text-sm flex-1 font-mono">
                  {apiKey.key.slice(0, 32)}...
                </code>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => copyToClipboard(apiKey.key)}
                >
                  <Copy className="h-4 w-4" />
                </Button>
              </div>

              <div className="flex items-center justify-between text-sm text-muted-foreground">
                <div className="flex items-center gap-4">
                  <span>Created: {new Date(apiKey.createdAt).toLocaleDateString()}</span>
                  {apiKey.expiresAt && (
                    <span>Expires: {new Date(apiKey.expiresAt).toLocaleDateString()}</span>
                  )}
                </div>
                {apiKey.lastUsed && (
                  <div className="flex items-center gap-1">
                    <Clock className="h-4 w-4" />
                    <span>Last used: {new Date(apiKey.lastUsed).toLocaleDateString()}</span>
                  </div>
                )}
              </div>

              {apiKey.status === 'active' && (
                <Button
                  variant="destructive"
                  onClick={() => onRevokeKey(apiKey.id)}
                >
                  Revoke Key
                </Button>
              )}
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}